import { getConfigTenant, isCorpTenant } from "@capone/common";
import { Box, Typography } from "@material-ui/core";
import clsx from "clsx";
import {
  B2BButton,
  BannerSeverity,
  DesktopPopupModal,
  Icon,
  IconName,
  MegaMenu,
  noop,
  NotificationBanner,
  PassengerCountPicker,
  PassengerCountPickerType,
} from "halifax";
import React, { ReactElement, useEffect, useMemo, useState } from "react";
import { RouteComponentProps } from "react-router";
import {
  IIdLodgings,
  IResult,
  MEGA_MENU_ENTRY,
  MegaMenuRootNode,
  NonEmptyArray,
  PackagesEntryTypeEnum,
  RecentPackageSearch,
  SliceStopCountFilter,
  TripCategory,
} from "redmond";
import { getPassengerCounts } from "../../utils/getPassengerCounts";
import { MultiAdultPassengerModal } from "../MultiAdultPassengerModal";
import {
  CalendarPickerButton,
  FareclassOptionSelection,
  PackagesSearchButton,
} from "./components";
import { OriginDestinationSearch } from "./components/OriginDestinationSearch";
import * as constants from "./constants";
import { PackagesSearchControlConnectorProps } from "./container";
import "./styles.scss";
import * as textConstants from "./textConstants";

import { config } from "../../../../api/config";
import { trackEvent } from "../../../../api/v0/trackEvent";
import {
  ACTION_BASED_DISCOVERY,
  AVAILABLE,
  getExperimentVariant,
  getExperimentVariantCustomVariants,
  LOCATION_BASED_DISCOVERY,
  PACKAGES_DISCOVERY_DRIVEN_SEARCH,
  PACKAGES_DISCOVERY_DRIVEN_SEARCH_VARIANTS,
  PACKAGES_FORCED_PAX,
  useExperiments,
  XMOD_V1_EXPERIMENT,
} from "../../../../context/experiments";
import { PATH_HOTELS_AVAILABILITY } from "../../../../utils/paths";
import { initialFareclassOptionFilter } from "../../../availability/reducer";
import { transformToStringifiedAvailabilityQuery } from "../../../availability/utils/queryStringHelpers";
import { ACTION_BASED_MEGA_MENU_ROOT_NODES } from "../../actionBasedMegaMenuRootNodes";
import { LOCATION_BASED_MEGA_MENU_ROOT_NODES } from "../../locationBasedMegaMenuRootNodes";
import { parseQueryString } from "../../utils/parseQueryString";
import { NonStopToggle } from "./components/NonstopToggle";
import { MEGA_MENU_CTA } from "./components/OriginDestinationSearch/constants";

export interface IPackagesSearchControlProps
  extends PackagesSearchControlConnectorProps,
    RouteComponentProps {
  showSearchButton?: boolean;
  showPackageSomeTest?: boolean;
  showMegaMenu?: boolean;
}

export const PackagesSearchControl = ({
  origin,
  setOrigin,
  destination,
  setDestination,
  departureDate,
  setDepartureDate,
  returnDate,
  setReturnDate,
  fareclassOptionFilter,
  setFareclassOptionFilter,
  hasSetFareclassFilter,
  stopsOption,
  setStopsOption,
  travelers,
  setTravelers,
  history,
  showSearchButton = true,
  datesModalOpen,
  setDatesModalOpen,
  showMegaMenu = true,
}: IPackagesSearchControlProps): ReactElement => {
  const [openPassengerCountPicker, setOpenPassengerCountPicker] =
    useState(false);
  const [showMultiAdultModal, setShowMultiAdultModal] = useState(false);
  const [hasMissingSearchInfoError, setHasMissingSearchInfoError] =
    useState(false);
  const [recentSearches, setRecentSearches] = useState<RecentPackageSearch[]>(
    [],
  );

  const isCorporate = isCorpTenant(getConfigTenant());

  const expState = useExperiments();
  const isForcedPAXExperiment =
    getExperimentVariant(expState.experiments, PACKAGES_FORCED_PAX) ===
    AVAILABLE;
  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;
  const packagesDiscoveryDrivenSearchVariant =
    getExperimentVariantCustomVariants(
      expState.experiments,
      PACKAGES_DISCOVERY_DRIVEN_SEARCH,
      PACKAGES_DISCOVERY_DRIVEN_SEARCH_VARIANTS,
    );

  const onRecentSearchClick = (recentSearch: RecentPackageSearch) => {
    const recentSearchInfants = [
      ...recentSearch.infantsInSeatAges.map((age) => ({ age, inSeat: true })),
      ...recentSearch.infantsInLapAges.map((age) => ({ age, inSeat: false })),
    ];

    const recentSearchFareClassOptionFilter =
      recentSearch.fareClass?.reduce(
        (acc, fareClass) => ({ ...acc, [fareClass]: true }),
        initialFareclassOptionFilter,
      ) || initialFareclassOptionFilter;

    history.push(
      `${PATH_HOTELS_AVAILABILITY}${transformToStringifiedAvailabilityQuery(
        recentSearch.originLocation,
        recentSearch.destinationLocationLabel,
        recentSearch.destinationLocation,
        recentSearch.departureDate,
        recentSearch.returnDate,
        recentSearch.numAdults,
        recentSearch.childrenAges,
        recentSearchInfants,
        SliceStopCountFilter.ANY_NUMBER,
        recentSearchFareClassOptionFilter,
        PackagesEntryTypeEnum.RECENTLY_SEARCHED_PACKAGE,
      )}`,
    );
  };

  useEffect(() => {
    if (
      !!destination &&
      !!origin &&
      !!departureDate &&
      !!returnDate &&
      !!travelers.adultsCount
    ) {
      setHasMissingSearchInfoError(false);
    }
  }, [destination, origin, departureDate, returnDate, travelers.adultsCount]);

  const parsedQueryString = parseQueryString(history);

  // We default to 1 traveler for corporate tenants
  React.useEffect(() => {
    if (isCorporate) {
      setTravelers({ adultsCount: 1, children: [], infants: [] });
    }
  }, [isCorporate]);

  useEffect(() => {
    if (
      !isForcedPAXExperiment &&
      !isCorporate &&
      !parsedQueryString?.entryPoint
    ) {
      setTravelers({ adultsCount: 2, children: [], infants: [] });
    }
  }, [isForcedPAXExperiment, isCorporate, parsedQueryString?.entryPoint]);

  useEffect(() => {
    const getRecentSearches = () => {
      try {
        const localStorageItem = localStorage.getItem(
          "recently_searched_packages",
        );

        if (localStorageItem) {
          const parsedSearches: RecentPackageSearch[] =
            JSON.parse(localStorageItem);
          const sortedSearches = parsedSearches
            .filter(
              (search) =>
                !!search.originLocation && !!search.destinationLocation,
            )
            .sort(
              (a, b) =>
                new Date(b.searchDate).getTime() -
                new Date(a.searchDate).getTime(),
            );
          setRecentSearches(sortedSearches);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(`Failed to get recent searches from local storage: ${e}`);
      }
    };
    getRecentSearches();
    // eslint-disable-next-line no-restricted-globals
    addEventListener("update_recently_searched_packages", getRecentSearches);
    return () =>
      // eslint-disable-next-line no-restricted-globals
      removeEventListener(
        "update_recently_searched_packages",
        getRecentSearches,
      );
  }, []);

  const [pendingTravelerCounts, setPendingTravelerCounts] =
    useState<PassengerCountPickerType | null>(null);

  const passengerCounts = useMemo(
    () => getPassengerCounts(pendingTravelerCounts, travelers),
    [pendingTravelerCounts, travelers],
  );

  const handleTravelersChanged = (counts: PassengerCountPickerType) => {
    setOpenPassengerCountPicker(false);
    if ("adultsCount" in counts) {
      if (counts.adultsCount > 1 && isCorporate) {
        setPendingTravelerCounts(counts);
        setShowMultiAdultModal(true);
      } else {
        setTravelers({
          adultsCount: counts.adultsCount,
          children: counts.childrenAges || [],
          infants: counts.infants || [],
        });
      }
    }
  };

  const incrementPackagesFunnelSearchesCounter = () => {
    const PACKAGES_FUNNEL_SEARCHES = "packages-funnel-searches";
    const searches = parseInt(
      sessionStorage.getItem(PACKAGES_FUNNEL_SEARCHES) || "0",
      10,
    );
    sessionStorage.setItem(PACKAGES_FUNNEL_SEARCHES, String(searches + 1));
  };

  const handleSearchClick = () => {
    if (
      !!destination &&
      !!origin &&
      !!departureDate &&
      !!returnDate &&
      !!travelers.adultsCount
    ) {
      incrementPackagesFunnelSearchesCounter();

      history.push(
        `${PATH_HOTELS_AVAILABILITY}${transformToStringifiedAvailabilityQuery(
          origin?.id?.code?.code,
          (destination?.id as IIdLodgings).lodgingSelection.searchTerm,
          (destination?.id as IIdLodgings).lodgingSelection.placeId,
          departureDate,
          returnDate,
          travelers.adultsCount,
          travelers.children,
          travelers.infants,
          stopsOption,
          fareclassOptionFilter,
        )}`,
      );
    } else {
      setHasMissingSearchInfoError(true);
    }
  };

  const renderTripTypeAndTravelerPickers = () => {
    const numTravelers =
      travelers.adultsCount +
      travelers.children.length +
      travelers.infants.length;
    const numTravelerString = numTravelers
      ? `${numTravelers} traveler${numTravelers === 1 ? "" : "s"}`
      : "Travelers";

    return (
      <Box className={clsx("traveler-pickers-container")}>
        <Box className="traveler-pickers">
          <B2BButton
            aria-label={numTravelerString}
            className={clsx("num-travelers-input b2b", {
              "has-error": !numTravelers && hasMissingSearchInfoError,
            })}
            variant="traveler-selector"
            onClick={() => setOpenPassengerCountPicker(true)}
          >
            <Box className="num-traveler-content">
              <Icon
                aria-hidden
                className="icon-start"
                name={IconName.B2BUser}
                ariaLabel=""
              />
              <Box className="text">{numTravelerString}</Box>
              <Icon
                aria-hidden
                className="icon-end"
                name={IconName.Dropdown}
                ariaLabel=""
              />
            </Box>
          </B2BButton>
        </Box>
        <DesktopPopupModal
          open={openPassengerCountPicker}
          className="packages-desktop-passenger-count-picker-popup"
          contentClassName="desktop-passenger-count-picker-popup-container"
          onClose={() => {
            setOpenPassengerCountPicker(false);
            setPendingTravelerCounts(null);
          }}
          invisibleBackdrop={false}
          headerElement={textConstants.EDIT_TRAVELERS_TITLE}
        >
          <PassengerCountPicker
            counts={passengerCounts}
            minimumAdultsCount={1}
            onClickApply={handleTravelersChanged}
            className="b2b"
            includeChildrenInMaxCount
            setPassengerCounts={noop}
            showChildAgeInputs
            showInfantAgeInputs
            titles={textConstants.TRAVELERS_SELECT_TITLES}
          />
        </DesktopPopupModal>

        <FareclassOptionSelection
          fareclassOptionFilter={fareclassOptionFilter}
          setFareclassOptionFilter={setFareclassOptionFilter}
          hasSetFareclassFilter={hasSetFareclassFilter}
          appliedLabel={Object.keys(fareclassOptionFilter).find(
            (key) => fareclassOptionFilter[key],
          )}
          labelIcon={IconName.FareIconFilled}
          includeClearFilter={false}
          popoverClassName="packages-search-fare-class-filter-popover"
        />

        <NonStopToggle
          checked={stopsOption === SliceStopCountFilter.NONE}
          onClick={(checked) => {
            setStopsOption(
              checked
                ? SliceStopCountFilter.NONE
                : SliceStopCountFilter.ANY_NUMBER,
            );
          }}
        />
      </Box>
    );
  };

  const handleMegaMenuSelection = (result: IResult) => {
    setDestination(result);
  };

  const getMegaMenuRootNodes = ():
    | NonEmptyArray<MegaMenuRootNode>
    | undefined => {
    if (packagesDiscoveryDrivenSearchVariant === LOCATION_BASED_DISCOVERY) {
      return LOCATION_BASED_MEGA_MENU_ROOT_NODES;
    } else if (
      packagesDiscoveryDrivenSearchVariant === ACTION_BASED_DISCOVERY
    ) {
      return ACTION_BASED_MEGA_MENU_ROOT_NODES;
    }
    return undefined;
  };

  const searchRowRef = React.useRef<HTMLDivElement>(null);

  const renderPackagesSearchRow = () => (
    <div ref={searchRowRef}>
      <Box className={clsx("packages-search-row")}>
        <OriginDestinationSearch
          origin={origin}
          setOrigin={setOrigin}
          destination={destination}
          setDestination={setDestination}
          hasMissingSearchInfoError={hasMissingSearchInfoError}
          recentSearches={recentSearches}
          onRecentSearchClick={onRecentSearchClick}
        />
        <CalendarPickerButton
          classes={["date-pickers"]}
          saveDatesOnClose
          tripCategory={TripCategory.ROUND_TRIP}
          departureDate={departureDate}
          returnDate={returnDate}
          setDepartureDate={setDepartureDate}
          setReturnDate={setReturnDate}
          hasMissingSearchInfoError={
            hasMissingSearchInfoError && !(departureDate && returnDate)
          }
          datesModalOpen={datesModalOpen}
          setDatesModalOpen={setDatesModalOpen}
        />
        {showSearchButton && (
          <PackagesSearchButton
            className={clsx("packages-search-button b2b", {
              xmod: isXmodRedesign,
            })}
            message={isCorporate ? constants.CORPORATE_SEARCH : constants.SEARCH}
            onClick={handleSearchClick}
            enabled
          />
        )}
      </Box>
    </div>
  );

  const renderErrorBanner = () => {
    if (!hasMissingSearchInfoError) return undefined;
    return (
      <Box className="missing-info-search-error-container">
        <NotificationBanner
          className="missing-info-search-error-banner"
          label={constants.MISSING_INFO_SEARCH_ERROR}
          severity={BannerSeverity.ERROR}
          icon={<Icon name={IconName.WarningAlert} />}
        />
      </Box>
    );
  };

  const shouldShowMegaMenu =
    showMegaMenu &&
    (packagesDiscoveryDrivenSearchVariant === LOCATION_BASED_DISCOVERY ||
      packagesDiscoveryDrivenSearchVariant === ACTION_BASED_DISCOVERY);

  const megaMenuRef = React.useRef<HTMLDivElement>(null);
  const [isMegaMenuActive, setIsMegaMenuActive] = React.useState(false);

  const handleMegaMenuCtaClick = () => {
    // Find and click the MegaMenu's NotificationBanner to trigger expansion
    const megaMenuBanner = megaMenuRef.current?.querySelector(
      ".notification-banner",
    ) as HTMLElement;
    if (megaMenuBanner) {
      megaMenuBanner.click();
      setIsMegaMenuActive(!isMegaMenuActive);
    }
  };

  return (
    <Box className={clsx("packages-search", config.TENANT)}>
      {renderTripTypeAndTravelerPickers()}
      {renderPackagesSearchRow()}
      {renderErrorBanner()}

      {shouldShowMegaMenu && (
        <Box className="mega-menu-container">
          <Box
            className={clsx("mega-menu-cta-button", {
              active: isMegaMenuActive,
            })}
            onClick={handleMegaMenuCtaClick}
          >
            <Icon name={IconName.Compass} />
            <Typography variant="body1">{MEGA_MENU_CTA}</Typography>
            <Icon name={IconName.RightChevronIcon} className="chevron" />
          </Box>
          <div ref={megaMenuRef} className="mega-menu-wrapper">
            <MegaMenu
              rootNodes={getMegaMenuRootNodes()}
              select={handleMegaMenuSelection}
              expandAllSubsections
              showAllOfOption={false}
              showCount={false}
              trackEvent={trackEvent}
              shards={[searchRowRef]}
              onExpandCollapse={(isExpanded) => {
                trackEvent({
                  eventName: MEGA_MENU_ENTRY,
                  properties: { is_expanded: isExpanded },
                });
              }}
            />
          </div>
        </Box>
      )}

      <MultiAdultPassengerModal
        open={showMultiAdultModal}
        onClose={() => {
          setShowMultiAdultModal(false);
          if (pendingTravelerCounts && "adultsCount" in pendingTravelerCounts) {
            setTravelers({
              adultsCount: pendingTravelerCounts.adultsCount,
              children: pendingTravelerCounts.childrenAges || [],
              infants: pendingTravelerCounts.infants || [],
            });
          }
          setPendingTravelerCounts(null);
        }}
        onEditTravelers={() => {
          setShowMultiAdultModal(false);
          setOpenPassengerCountPicker(true);
        }}
      />
    </Box>
  );
};
